import { Stats, styled, Text, Tooltip } from '@meterup/atto';

import type { TimeFormatValue, Timestamp } from '../topics/time';
import {
  type TooltipTimezone,
  useTimestampDisplayContext,
} from '../providers/TimestampDisplayContext';
import { formatDateTime, parseDateTime, TimeFormat } from '../topics/time';
import { NoValue } from './NoValue';

export type { TooltipTimezone };

/**
 * Renders multi-timezone tooltip content for a timestamp. Use as Tooltip contents when the
 * trigger is not the timestamp itself (e.g. a Badge).
 */
export function TimestampTooltipContent({
  value,
  tooltipTimezones: tooltipTimezonesProp,
}: {
  value: Timestamp | null | undefined;
  tooltipTimezones?: TooltipTimezone[];
}) {
  const context = useTimestampDisplayContext();
  if (!value) return null;
  const dt = parseDateTime(value);
  if (!dt) return null;

  const tooltipTimezones = tooltipTimezonesProp ?? context?.tooltipTimezones;
  if (tooltipTimezones && tooltipTimezones.length > 0) {
    return (
      <Stats
        direction="column"
        gutter={{ edge: 'none' }}
        size="small"
        stats={tooltipTimezones.map(({ zone, label }) => ({
          label,
          value: (
            <Text>
              {formatDateTime(value, TimeFormat.LONG, zone)}{' '}
              {formatDateTime(value, TimeFormat.U_NAMED_OFFSET, zone)}
            </Text>
          ),
        }))}
      />
    );
  }
  return (
    <Text>
      {formatDateTime(dt, TimeFormat.LONG)} {formatDateTime(dt, TimeFormat.U_NAMED_OFFSET)}
    </Text>
  );
}

export const FormatDateTimeTooltip = styled('div', {
  display: 'flex',
  padding: '$8',
});
FormatDateTimeTooltip.displayName = 'FormatDateTimeTooltip';

/**
 * Displays date time with the given format, with a tooltip displaying the locale-full timestamp.
 * Optional displayZone and tooltipTimezones (from props or TimestampDisplayContext) enable
 * showing timestamps in network timezone, UTC, and user local time.
 */
export function FormatDateTime({
  value,
  format = TimeFormat.MEDIUM,
  displayZone: displayZoneProp,
  tooltipTimezones: tooltipTimezonesProp,
  size,
}: {
  value: Timestamp | null | undefined;
  format?: TimeFormat | TimeFormatValue;
  displayZone?: string;
  tooltipTimezones?: TooltipTimezone[];
  size?: React.ComponentProps<typeof Text>['size'];
}) {
  const context = useTimestampDisplayContext();
  const displayZone = displayZoneProp ?? context?.displayZone;
  const tooltipTimezones = tooltipTimezonesProp ?? context?.tooltipTimezones;

  if (!value) return <NoValue />;
  const dt = parseDateTime(value);
  if (!dt) return <NoValue />;

  const displayText = formatDateTime(dt, format, displayZone);

  if (!value) return <NoValue />;

  return (
    <Tooltip
      tight
      contents={
        <FormatDateTimeTooltip>
          <TimestampTooltipContent value={value} tooltipTimezones={tooltipTimezones} />
        </FormatDateTimeTooltip>
      }
    >
      <Text size={size}>{displayText}</Text>
    </Tooltip>
  );
}
